// =============================================================================
// Automations Store - V3-2
// Gestion des automatisations projet avec optimistic updates
// =============================================================================

import { create } from 'zustand';
import {
  fetchAutomations,
  createAutomation,
  updateAutomation,
  toggleAutomation,
  deleteAutomation,
  findMatchingAutomations,
  type Automation,
  type AutomationTriggerType,
  type AutomationAction,
  type AutomationConditions,
  type CreateAutomationInput,
} from '../data/automationRepository';

interface AutomationsState {
  automations: Automation[];
  loading: boolean;
  error: string | null;
  
  // Actions
  loadAutomations: (orgId: string, projectId: string) => Promise<void>;
  addAutomation: (input: CreateAutomationInput) => Promise<Automation>;
  editAutomation: (automationId: string, updates: Partial<Pick<Automation, 'name' | 'description' | 'trigger_type' | 'conditions' | 'actions' | 'enabled'>>) => Promise<void>;
  toggleEnabled: (automationId: string, enabled: boolean) => Promise<void>;
  removeAutomation: (automationId: string) => Promise<void>;
  clearAutomations: () => void;
  
  // Trigger execution helper
  findMatchingForTrigger: (
    orgId: string,
    projectId: string,
    triggerType: AutomationTriggerType,
    context: { from_status?: string; to_status?: string; tag_id?: string; assignee_id?: string }
  ) => Promise<Automation[]>;
  
  // Helpers
  getAutomationById: (automationId: string) => Automation | undefined;
  getEnabledAutomations: () => Automation[];
}

export const useAutomationsStore = create<AutomationsState>((set, get) => ({
  automations: [],
  loading: false,
  error: null,

  loadAutomations: async (orgId, projectId) => {
    set({ loading: true, error: null });
    try {
      const automations = await fetchAutomations(orgId, projectId);
      set({ automations, loading: false });
    } catch (err) {
      console.error('[useAutomationsStore] loadAutomations error:', err);
      set({ error: 'Erreur lors du chargement des automatisations', loading: false });
    }
  },

  addAutomation: async (input) => {
    const tempId = `temp-${Date.now()}`;
    const optimisticAutomation: Automation = {
      id: tempId,
      org_id: input.org_id,
      project_id: input.project_id,
      name: input.name,
      description: input.description || null,
      trigger_type: input.trigger_type,
      conditions: input.conditions || {},
      actions: input.actions,
      enabled: input.enabled ?? true,
      created_by: input.created_by || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Optimistic add
    set(state => ({
      automations: [...state.automations, optimisticAutomation],
    }));

    try {
      const created = await createAutomation(input);
      
      // Replace temp with real
      set(state => ({
        automations: state.automations.map(a => 
          a.id === tempId ? created : a
        ),
      }));
      
      return created;
    } catch (err) {
      // Rollback
      set(state => ({
        automations: state.automations.filter(a => a.id !== tempId),
      }));
      throw err;
    }
  },

  editAutomation: async (automationId, updates) => {
    const original = get().automations.find(a => a.id === automationId);
    if (!original) return;

    // Optimistic update
    set(state => ({
      automations: state.automations.map(a =>
        a.id === automationId ? { ...a, ...updates, updated_at: new Date().toISOString() } : a
      ),
    }));

    try {
      await updateAutomation(automationId, updates);
    } catch (err) {
      // Rollback
      set(state => ({
        automations: state.automations.map(a =>
          a.id === automationId ? original : a
        ),
      }));
      throw err;
    }
  },

  toggleEnabled: async (automationId, enabled) => {
    const original = get().automations.find(a => a.id === automationId);
    if (!original) return;

    // Optimistic toggle
    set(state => ({
      automations: state.automations.map(a =>
        a.id === automationId ? { ...a, enabled } : a
      ),
    }));

    try {
      await toggleAutomation(automationId, enabled);
    } catch (err) {
      // Rollback
      set(state => ({
        automations: state.automations.map(a =>
          a.id === automationId ? original : a
        ),
      }));
      throw err;
    }
  },

  removeAutomation: async (automationId) => {
    const original = get().automations.find(a => a.id === automationId);
    if (!original) return;

    // Optimistic remove
    set(state => ({
      automations: state.automations.filter(a => a.id !== automationId),
    }));

    try {
      await deleteAutomation(automationId);
    } catch (err) {
      // Rollback
      set(state => ({
        automations: [...state.automations, original],
      }));
      throw err;
    }
  },

  clearAutomations: () => {
    set({ automations: [], loading: false, error: null });
  },

  findMatchingForTrigger: async (orgId, projectId, triggerType, context) => {
    return findMatchingAutomations(orgId, projectId, triggerType, context);
  },

  getAutomationById: (automationId) => {
    return get().automations.find(a => a.id === automationId);
  },

  getEnabledAutomations: () => {
    return get().automations.filter(a => a.enabled);
  },
}));

// Export types
export type { 
  Automation, 
  AutomationTriggerType, 
  AutomationAction, 
  AutomationConditions, 
  CreateAutomationInput 
};
